import { useCallback, useEffect, useState } from 'react'

const DISMISSED_KEY = 'ekklesia_install_dismissed'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export type InstallMode = 'none' | 'prompt' | 'ios-instructions'

export interface UseInstallPrompt {
  /** `prompt` sur Android/Chrome, `ios-instructions` sur iPhone, sinon `none`. */
  mode: InstallMode
  install: () => Promise<void>
  dismiss: () => void
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // Safari iOS n'implémente pas display-mode et expose son propre indicateur.
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent)
}

/**
 * Invite à l'installation.
 *
 * Android/Chrome fournit `beforeinstallprompt`, qu'on intercepte pour choisir
 * le moment. Safari iOS n'a pas d'équivalent : il faut guider l'utilisateur
 * vers « Partager → Sur l'écran d'accueil ».
 */
export function useInstallPrompt(): UseInstallPrompt {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISSED_KEY) === '1')
  const [installed, setInstalled] = useState(() => isStandalone())

  useEffect(() => {
    function handleBeforeInstall(event: Event) {
      // Sans preventDefault, Chrome affiche sa propre bannière au moment
      // qu'il choisit.
      event.preventDefault()
      setDeferred(event as BeforeInstallPromptEvent)
    }

    function handleInstalled() {
      setInstalled(true)
      setDeferred(null)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstall)
    window.addEventListener('appinstalled', handleInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall)
      window.removeEventListener('appinstalled', handleInstalled)
    }
  }, [])

  const install = useCallback(async () => {
    if (!deferred) return
    await deferred.prompt()
    await deferred.userChoice
    setDeferred(null)
  }, [deferred])

  const dismiss = useCallback(() => {
    // Préférence d'affichage, pas un secret : localStorage convient.
    localStorage.setItem(DISMISSED_KEY, '1')
    setDismissed(true)
  }, [])

  let mode: InstallMode = 'none'
  if (!installed && !dismissed) {
    if (deferred) mode = 'prompt'
    else if (isIos()) mode = 'ios-instructions'
  }

  return { mode, install, dismiss }
}
